import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { STUDIO_TYPE_LABELS } from '@conversed/shared/src/central/types/studio.type';
import { useDataSourceActions } from '@hooks/dataSources/useDataSourceActions';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useMainSelector } from '@hooks/useMainSelector';
import { usePermissions } from '@hooks/usePermissions';
import type { TCompany } from '@interfaces/IAuth';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type InfoDataSourceDownloadButtonProps = {
  fileOwnerStudio?: TCompany;
};

export const InfoDataSourceDownloadButton: React.FC<
  InfoDataSourceDownloadButtonProps
> = ({ fileOwnerStudio }) => {
  const {
    user: { currentUser },
  } = useMainSelector();

  const { selectedDataSourceFile } = useDataSources();
  const { selectedCompany } = useCompanySelector();

  const { downloadDataSource } = useDataSourceActions();
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();

  const isOwner = useMemo(
    () => selectedDataSourceFile?.companyId === selectedCompany?.id,
    [selectedDataSourceFile?.companyId, selectedCompany?.id],
  );

  const isDataSourceDownloadable = useMemo(() => {
    // * If the field is not set default to 'true'
    return selectedDataSourceFile?.canBeDownloaded !== false;
  }, [selectedDataSourceFile?.canBeDownloaded]);

  const hasShowDownloadPermission = useMemo(() => {
    return hasPermissions([PERMISSIONS.DATA_SOURCE.DATA_SOURCE_DOWNLOAD]);
  }, [hasPermissions]);

  const isSuperAdmin = useMemo(() => {
    return currentUser?.isSuperAdmin;
  }, [currentUser]);

  if (
    // * always allow downloads for superadmin
    !isSuperAdmin &&
    // * if no download permission then hide button, also hide if file cannot be downloaded
    (!hasShowDownloadPermission || !isDataSourceDownloadable)
  ) {
    return <></>;
  }

  // * if not the owner, do not allow download, show "download is not available here message"
  if (!isOwner) {
    return (
      <div className='flex items-center font-normal text-tiny text-gray-400 italic mt-2 gap-1'>
        <MaterialIcon icon='info' classes={['text-primary-base text-base']} />
        {fileOwnerStudio?.studioType
          ? t('data-sources.sidebar.file-details.not-owner-download-warning', {
              studioName: STUDIO_TYPE_LABELS[fileOwnerStudio.studioType],
            })
          : t(
              'data-sources.sidebar.file-details.not-owner-download-warning-general',
            )}
      </div>
    );
  }

  // * show the download button
  return (
    <ButtonT
      type='button'
      styling='primary'
      size='xs'
      rounding='md'
      onClick={() => {
        const _id = selectedDataSourceFile?._id?.toString();
        const fileKey = selectedDataSourceFile?.key ?? '';
        if (_id) {
          downloadDataSource(
            _id,
            fileKey,
            selectedDataSourceFile?.thirdPartySource || undefined,
          );
        }
      }}>
      {t('general.buttons.download')}
    </ButtonT>
  );
};
